// نمایش و تکمیل فرم از تعریف JSON (docs/08-engineering/form-definition-schema.md؛ AC-DOC-020.1، 021.1): همه انواع فیلد، منطق شرطی سمت
// کاربر (همان قواعد سرور؛ سرور دوباره اعتبارسنجی می‌کند)، محاسبه‌ای‌ها فقط نمایشی. مشترک بین کارکنان (SCR-STAFF-602)، تبلت (SCR-TABLET-001)
// و لینک عمومی (SCR-PUBLIC-010).
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from '@phoenix/i18n';
import { faDigits } from '../format/digits';
import { useId } from 'react';
import { SignaturePad } from './SignaturePad';

export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
export type Answers = Record<string, JsonValue>;

export interface LocalizedText {
  fa?: string;
  en?: string;
  ar?: string;
}

export interface FieldCondition {
  field: string;
  op: string;
  value?: JsonValue;
}

export interface FormFieldDef {
  key: string;
  type: string;
  label?: LocalizedText;
  text?: LocalizedText;
  help?: LocalizedText;
  required?: boolean;
  sensitivity?: string;
  validation?: { min?: number; max?: number; pattern?: string; maxLength?: number };
  scale?: { min?: number; max?: number };
  options?: { value: string; label?: LocalizedText; score?: number }[];
  columns?: { key: string; label?: LocalizedText; type?: string }[];
  formula?: { op?: string; fields: string[] };
  visibleIf?: FieldCondition;
  mapTo?: string;
}

export interface FormSectionDef {
  key: string;
  title?: LocalizedText;
  titleKey?: string;
  fields: FormFieldDef[];
}

/** امتیازدهی پرسشنامه (US-DOC-022؛ form-definition-schema §scoring). */
export interface ScoringBandDef {
  code: string;
  label?: LocalizedText;
  min: number;
  max: number;
  severity?: string;
}

export interface ScoringScaleDef {
  key: string;
  label?: LocalizedText;
  op?: string;
  items: string[];
  primary?: boolean;
  bands?: ScoringBandDef[];
}

export interface ScoringDef {
  instrument: string;
  version: string;
  license: { status: string; holder?: string; note?: string };
  scales: ScoringScaleDef[];
  flags?: { code: string; label?: LocalizedText; item: string; min: number }[];
}

export interface FormDefinitionJson {
  schemaVersion: number;
  sections: FormSectionDef[];
  scoring?: ScoringDef;
}

export interface PatientContext {
  gender?: string | null;
  age?: number | string | null;
}

export const FIELD_TYPES = [
  'shortText',
  'longText',
  'number',
  'date',
  'singleChoice',
  'multiChoice',
  'yesNo',
  'scale',
  'table',
  'file',
  'signature',
  'bodyMap',
  'calculated',
  'info',
] as const;

export function asDefinition(value: unknown): FormDefinitionJson {
  if (value && typeof value === 'object' && Array.isArray((value as FormDefinitionJson).sections)) {
    return value as FormDefinitionJson;
  }
  return { schemaVersion: 1, sections: [] };
}

export function textOf(value: LocalizedText | undefined, language: string, fallback = ''): string {
  if (!value) {
    return fallback;
  }
  return (language === 'en' ? value.en : language === 'ar' ? value.ar : value.fa) ?? value.fa ?? value.en ?? fallback;
}

function isEmpty(value: JsonValue | undefined): boolean {
  return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
}

function compareValue(left: JsonValue | undefined, right: JsonValue | undefined): boolean {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

/** ارزیابی شرط (هم‌ارز FormDefinition.Evaluate سرور). */
export function evaluate(condition: FieldCondition, answers: Answers, patient: PatientContext): boolean {
  let actual: JsonValue | undefined;
  if (condition.field === 'patient.gender') {
    actual = patient.gender ?? null;
  } else if (condition.field === 'patient.age') {
    actual = patient.age === null || patient.age === undefined ? null : Number(patient.age);
  } else {
    actual = answers[condition.field];
  }
  switch (condition.op) {
    case 'truthy':
      return !isEmpty(actual) && actual !== false;
    case 'eq':
      return compareValue(actual, condition.value);
    case 'ne':
      return !compareValue(actual, condition.value);
    case 'in':
      return Array.isArray(condition.value) && condition.value.some((option) => compareValue(actual, option));
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      if (typeof actual !== 'number' || typeof condition.value !== 'number') {
        return false;
      }
      return condition.op === 'gt'
        ? actual > condition.value
        : condition.op === 'gte'
          ? actual >= condition.value
          : condition.op === 'lt'
            ? actual < condition.value
            : actual <= condition.value;
    }
    default:
      return false;
  }
}

/** فیلدهای نمایان با پاسخ‌های فعلی. */
export function visibleFields(definition: FormDefinitionJson, answers: Answers, patient: PatientContext): FormFieldDef[] {
  const visible: FormFieldDef[] = [];
  for (const section of definition.sections) {
    for (const field of section.fields) {
      if (!field.visibleIf || evaluate(field.visibleIf, answers, patient)) {
        visible.push(field);
      }
    }
  }
  return visible;
}

/** کلید فیلدهای الزامی نمایان بدون پاسخ (پیش از ارسال؛ سرور هم بررسی می‌کند). */
export function missingRequired(definition: FormDefinitionJson, answers: Answers, patient: PatientContext): string[] {
  return visibleFields(definition, answers, patient)
    .filter((field) => field.required && field.type !== 'info' && field.type !== 'calculated' && isEmpty(answers[field.key]))
    .map((field) => field.key);
}

/** پاسخ‌های فیلدهای پنهان کنار گذاشته می‌شوند. */
export function cleanAnswers(definition: FormDefinitionJson, answers: Answers, patient: PatientContext): Answers {
  const keys = new Set(visibleFields(definition, answers, patient).map((field) => field.key));
  return Object.fromEntries(Object.entries(answers).filter(([key, value]) => keys.has(key) && !isEmpty(value)));
}

function calculate(field: FormFieldDef, answers: Answers): number | null {
  const values = (field.formula?.fields ?? []).map((key) => answers[key]).filter((value): value is number => typeof value === 'number');
  if (values.length === 0) {
    return null;
  }
  switch (field.formula?.op ?? 'sum') {
    case 'avg':
      return values.reduce((a, b) => a + b, 0) / values.length;
    case 'min':
      return Math.min(...values);
    case 'max':
      return Math.max(...values);
    default:
      return values.reduce((a, b) => a + b, 0);
  }
}

interface FieldProps {
  field: FormFieldDef;
  value: JsonValue | undefined;
  onChange: (value: JsonValue | null) => void;
  answers: Answers;
  disabled: boolean;
  error: boolean;
  testPrefix: string;
}

function FieldInput({ field, value, onChange, answers, disabled, error, testPrefix }: FieldProps) {
  const { t, i18n } = useTranslation('doc');
  const id = useId();
  const language = i18n.language;
  const label = textOf(field.label, language, field.key) + (field.required ? ' *' : '');
  const help = field.help ? textOf(field.help, language) : undefined;
  const testId = `${testPrefix}-${field.key}`;
  const errorText = error ? t('doc:form.required') : help;

  switch (field.type) {
    case 'info':
      return (
        <Typography color="text.secondary" data-testid={testId}>
          {textOf(field.text ?? field.label, language)}
        </Typography>
      );
    case 'shortText':
    case 'longText':
    case 'date':
      return (
        <TextField
          label={label}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
          multiline={field.type === 'longText'}
          minRows={field.type === 'longText' ? 3 : undefined}
          type={field.type === 'date' ? 'date' : 'text'}
          disabled={disabled}
          error={error}
          helperText={errorText}
          slotProps={{
            htmlInput: { 'data-testid': testId, maxLength: field.validation?.maxLength },
            inputLabel: field.type === 'date' ? { shrink: true } : undefined,
          }}
        />
      );
    case 'number':
      return (
        <TextField
          label={label}
          value={typeof value === 'number' ? String(value) : ''}
          onChange={(event) => {
            const text = event.target.value.trim();
            onChange(text === '' || Number.isNaN(Number(text)) ? null : Number(text));
          }}
          inputMode="decimal"
          disabled={disabled}
          error={error}
          helperText={errorText}
          slotProps={{ htmlInput: { 'data-testid': testId } }}
        />
      );
    case 'yesNo':
      return (
        <FormControl error={error} disabled={disabled}>
          <FormLabel id={id}>{label}</FormLabel>
          <RadioGroup
            row
            aria-labelledby={id}
            value={value === true ? 'yes' : value === false ? 'no' : ''}
            onChange={(event) => onChange(event.target.value === 'yes')}
            data-testid={testId}
          >
            <FormControlLabel value="yes" control={<Radio slotProps={{ input: { 'data-testid': `${testId}-yes` } as never }} />} label={t('doc:form.yes')} />
            <FormControlLabel value="no" control={<Radio slotProps={{ input: { 'data-testid': `${testId}-no` } as never }} />} label={t('doc:form.no')} />
          </RadioGroup>
          {errorText ? <FormHelperText>{errorText}</FormHelperText> : null}
        </FormControl>
      );
    case 'singleChoice':
      return (
        <FormControl error={error} disabled={disabled}>
          <FormLabel id={id}>{label}</FormLabel>
          <RadioGroup aria-labelledby={id} value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value)} data-testid={testId}>
            {(field.options ?? []).map((option) => (
              <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={textOf(option.label, language, option.value)} />
            ))}
          </RadioGroup>
          {errorText ? <FormHelperText>{errorText}</FormHelperText> : null}
        </FormControl>
      );
    case 'multiChoice': {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <FormControl error={error} disabled={disabled} component="fieldset">
          <FormLabel component="legend">{label}</FormLabel>
          <FormGroup data-testid={testId}>
            {(field.options ?? []).map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={selected.includes(option.value)}
                    onChange={(event) =>
                      onChange(event.target.checked ? [...selected, option.value] : selected.filter((item) => item !== option.value))
                    }
                  />
                }
                label={textOf(option.label, language, option.value)}
              />
            ))}
          </FormGroup>
          {errorText ? <FormHelperText>{errorText}</FormHelperText> : null}
        </FormControl>
      );
    }
    case 'scale': {
      const min = field.scale?.min ?? field.validation?.min ?? 0;
      const max = field.scale?.max ?? field.validation?.max ?? 10;
      return (
        <Box>
          <Typography id={id} gutterBottom>
            {label}
          </Typography>
          <Slider
            aria-labelledby={id}
            value={typeof value === 'number' ? value : min}
            min={min}
            max={max}
            step={1}
            marks
            valueLabelDisplay="auto"
            valueLabelFormat={(number) => faDigits(number)}
            onChange={(_event, next) => onChange(next as number)}
            disabled={disabled}
            data-testid={testId}
          />
          {errorText ? <FormHelperText error={error}>{errorText}</FormHelperText> : null}
        </Box>
      );
    }
    case 'calculated': {
      const result = calculate(field, answers);
      return (
        <Typography data-testid={testId}>
          {label}: <strong>{result === null ? '—' : faDigits(Number.isInteger(result) ? result : result.toFixed(2))}</strong>
        </Typography>
      );
    }
    case 'table': {
      const rows = Array.isArray(value) ? (value as Record<string, JsonValue>[]) : [];
      const update = (index: number, key: string, cell: string, numeric: boolean) => {
        const next = rows.map((row, position) =>
          position === index ? { ...row, [key]: numeric ? (cell === '' ? null : Number(cell)) : cell } : row,
        );
        onChange(next);
      };
      return (
        <Stack spacing={1} data-testid={testId}>
          <Typography>{label}</Typography>
          {rows.map((row, index) => (
            <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              {(field.columns ?? []).map((column) => (
                <TextField
                  key={column.key}
                  size="small"
                  label={textOf(column.label, language, column.key)}
                  value={row[column.key] === null || row[column.key] === undefined ? '' : String(row[column.key])}
                  onChange={(event) => update(index, column.key, event.target.value, column.type === 'number')}
                  disabled={disabled}
                />
              ))}
              <IconButton aria-label={t('doc:form.remove_row')} onClick={() => onChange(rows.filter((_, position) => position !== index))} disabled={disabled}>
                <DeleteOutlineIcon />
              </IconButton>
            </Stack>
          ))}
          <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => onChange([...rows, {}])} disabled={disabled} data-testid={`${testId}-add`}>
            {t('doc:form.add_row')}
          </Button>
        </Stack>
      );
    }
    case 'file':
      return (
        <TextField
          label={label}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => onChange(event.target.value === '' ? null : event.target.value)}
          disabled={disabled}
          error={error}
          helperText={errorText ?? t('doc:form.file_hint')}
          slotProps={{ htmlInput: { 'data-testid': testId, dir: 'ltr' } }}
        />
      );
    case 'signature':
      return disabled ? (
        <Typography data-testid={testId}>{label}: {value ? t('doc:form.signed') : '—'}</Typography>
      ) : (
        <Box>
          <SignaturePad label={label} testId={testId} onChange={(signature) => onChange(signature ? signature.image : null)} />
          {error ? <FormHelperText error>{t('doc:form.required')}</FormHelperText> : null}
        </Box>
      );
    case 'bodyMap': {
      const marks = value && typeof value === 'object' && !Array.isArray(value) && Array.isArray((value as { marks?: JsonValue[] }).marks)
        ? ((value as { marks: { x: number; y: number }[] }).marks)
        : [];
      return (
        <Stack spacing={0.5}>
          <Typography>{label}</Typography>
          <Box
            role="img"
            aria-label={label}
            data-testid={testId}
            data-marks={String(marks.length)}
            onClick={(event) => {
              if (disabled) {
                return;
              }
              const rect = event.currentTarget.getBoundingClientRect();
              const x = rect.width ? (event.clientX - rect.left) / rect.width : 0.5;
              const y = rect.height ? (event.clientY - rect.top) / rect.height : 0.5;
              onChange({ marks: [...marks, { x: Math.min(1, Math.max(0, x)), y: Math.min(1, Math.max(0, y)) }] });
            }}
            sx={{ position: 'relative', width: 180, height: 300, border: 1, borderColor: 'divider', borderRadius: 1, cursor: disabled ? 'default' : 'crosshair' }}
          >
            <svg viewBox="0 0 100 200" width="100%" height="100%" aria-hidden="true">
              <circle cx="50" cy="20" r="14" fill="none" stroke="currentColor" />
              <rect x="32" y="36" width="36" height="70" rx="10" fill="none" stroke="currentColor" />
              <line x1="32" y1="45" x2="12" y2="100" stroke="currentColor" />
              <line x1="68" y1="45" x2="88" y2="100" stroke="currentColor" />
              <line x1="42" y1="106" x2="38" y2="190" stroke="currentColor" />
              <line x1="58" y1="106" x2="62" y2="190" stroke="currentColor" />
            </svg>
            {marks.map((mark, index) => (
              <Box key={index} sx={{ position: 'absolute', left: `${mark.x * 100}%`, top: `${mark.y * 100}%`, width: 10, height: 10, ml: '-5px', mt: '-5px', borderRadius: '50%', bgcolor: 'error.main' }} />
            ))}
          </Box>
          {!disabled && marks.length > 0 ? (
            <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => onChange(null)}>
              {t('doc:form.clear_marks')}
            </Button>
          ) : null}
        </Stack>
      );
    }
    default:
      return null;
  }
}

export interface FormRendererProps {
  definition: FormDefinitionJson;
  answers: Answers;
  onChange: (answers: Answers) => void;
  patient: PatientContext;
  disabled?: boolean;
  errors?: readonly string[];
  testPrefix?: string;
}

export function FormRenderer({ definition, answers, onChange, patient, disabled = false, errors = [], testPrefix = 'form-field' }: FormRendererProps) {
  const { i18n } = useTranslation('doc');
  return (
    <Stack spacing={2} data-testid="form-renderer">
      {definition.sections.map((section) => {
        const fields = section.fields.filter((field) => !field.visibleIf || evaluate(field.visibleIf, answers, patient));
        if (fields.length === 0) {
          return null;
        }
        return (
          <Paper key={section.key} variant="outlined" sx={{ p: 2 }} component="section" aria-label={textOf(section.title, i18n.language, section.key)}>
            <Typography variant="h6" component="h3" gutterBottom>
              {textOf(section.title, i18n.language, section.key)}
            </Typography>
            <Stack spacing={2}>
              {fields.map((field) => (
                <FieldInput
                  key={field.key}
                  field={field}
                  value={answers[field.key]}
                  answers={answers}
                  disabled={disabled}
                  error={errors.includes(field.key)}
                  testPrefix={testPrefix}
                  onChange={(value) => {
                    const next = { ...answers };
                    if (value === null) {
                      delete next[field.key];
                    } else {
                      next[field.key] = value;
                    }
                    onChange(next);
                  }}
                />
              ))}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
